import { API_URL } from '../../../../constants/urls.constants';
import type {
  CommonResponse,
  FailedJobResponse,
  FailedJobsParams,
  GetQueryArgsType,
} from '../../../../types/global.types';
import { appServiceApi } from '../../../apis/app-service.api';
import { APP_API_TAGS } from '../../../app.constants';
import type {
  ActiveCustomFieldsResponse,
  CustomEmployeeFieldListResponse,
  CustomEmployeeFieldParams,
  UpsertCustomFieldBody,
  AllOfficesResponse,
  CompanyDetailsResponse,
  FailedOfficeListParams,
  FailedOfficeListResponse,
  GetAllOfficesParams,
  OfficeResponse,
  UpdateCompanyParams,
} from './organization.type';

// Import department and designation hooks from emp-dashboard

const { OFFICES, CUSTOM_EMPLOYEE_FIELDS } = APP_API_TAGS || {};

export const organizationApi = appServiceApi.injectEndpoints({
  endpoints: (builder) => ({
    // ---------------------------------------------------------------
    // Custom employee fields (JTI step 7)
    // ---------------------------------------------------------------

    getCustomEmployeeFields: builder.query<
      CustomEmployeeFieldListResponse,
      GetQueryArgsType<CustomEmployeeFieldParams>
    >({
      query: (queryArgs) => ({
        url: API_URL.customEmployeeFields,
        method: 'GET',
        ...queryArgs,
      }),
      providesTags: [CUSTOM_EMPLOYEE_FIELDS],
    }),

    /** Flat list of active fields — what the employee screens render. */
    getActiveCustomEmployeeFields: builder.query<ActiveCustomFieldsResponse, void>(
      {
        query: () => ({
          url: `${API_URL.customEmployeeFields}/active`,
          method: 'GET',
        }),
        providesTags: [CUSTOM_EMPLOYEE_FIELDS],
      },
    ),

    saveCustomEmployeeField: builder.mutation<any, UpsertCustomFieldBody>({
      query: ({ id, ...body }) => ({
        url: id
          ? `${API_URL.customEmployeeFields}/${id}`
          : API_URL.customEmployeeFields,
        method: id ? 'PATCH' : 'POST',
        body,
      }),
      invalidatesTags: (res) => (res ? [CUSTOM_EMPLOYEE_FIELDS] : []),
    }),

    /** Soft delete: the field leaves the forms, stored values stay intact. */
    deleteCustomEmployeeField: builder.mutation<any, { id: string }>({
      query: ({ id }) => ({
        url: `${API_URL.customEmployeeFields}/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (res) => (res ? [CUSTOM_EMPLOYEE_FIELDS] : []),
    }),

    // ------ Start: API Endpoints for Office Management ------
    createOffice: builder.mutation({
      query: (data) => ({
        url: API_URL.offices,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [OFFICES],
    }),

    updateOffice: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `${API_URL.offices}/${id}`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: [OFFICES],
    }),

    deleteOffice: builder.mutation({
      query: (id) => ({
        url: `${API_URL.offices}/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [OFFICES],
    }),

    getOfficeById: builder.query<OfficeResponse, string>({
      query: (id) => ({
        url: `${API_URL.offices}/${id}`,
        method: 'GET',
      }),
      providesTags: [OFFICES],
    }),

    getAllOffices: builder.query<
      AllOfficesResponse,
      GetQueryArgsType<GetAllOfficesParams>
    >({
      query: (queryArgs) => ({
        url: API_URL.offices,
        method: 'GET',
        ...queryArgs,
      }),
      providesTags: [OFFICES],
    }),
    // ------ END: API Endpoints for Office Management ------

    // ------ Start: API Endpoints for Company Management ------
    updateCompany: builder.mutation({
      query: (data: UpdateCompanyParams) => ({
        url: `${API_URL.organizations}/settings`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: [OFFICES], // Assuming company update affects offices
    }),

    getCompanyDetails: builder.query<CompanyDetailsResponse, void>({
      query: () => ({
        url: `${API_URL.organizations}/settings`,
        method: 'GET',
      }),
      providesTags: [OFFICES],
    }),
    // ------ END: API Endpoints for Company Management ------

    // ------ Start: API Endpoints for Bulk Office Upload ------
    downloadOfficesTemplate: builder.query<string, void>({
      query: () => ({
        url: `${API_URL.offices}/upload/template`,
        responseHandler: async (response: Response) => {
          const blob = await response.blob();
          return window.URL.createObjectURL(blob);
        },
        cache: 'no-cache',
      }),
    }),
    uploadOffices: builder.mutation<CommonResponse<any>, FormData>({
      query: (body) => ({
        url: `${API_URL.offices}/upload`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (res) => (res ? [OFFICES] : []),
    }),
    getFailedOfficesJobs: builder.query<
      CommonResponse<FailedJobResponse>,
      GetQueryArgsType<FailedJobsParams>
    >({
      query: (queryArgs) => ({
        url: `${API_URL.offices}/failed-offices/jobs`,
        ...queryArgs,
      }),
      providesTags: [OFFICES],
    }),
    getFailedOfficesRecords: builder.query<
      CommonResponse<FailedOfficeListResponse>,
      GetQueryArgsType<FailedOfficeListParams>
    >({
      query: (queryArgs) => ({
        url: `${API_URL.offices}/failed-offices/list`,
        ...queryArgs,
      }),
      providesTags: [OFFICES],
    }),
    // ------ END: API Endpoints for Bulk Office Upload ------
  }),
});

// Export organization API hooks
export const {
  // Office Management
  useCreateOfficeMutation,
  useUpdateOfficeMutation,
  useDeleteOfficeMutation,
  useGetOfficeByIdQuery,
  useGetAllOfficesQuery,
  // Company Management
  useUpdateCompanyMutation,
  useGetCompanyDetailsQuery,
  // Bulk Office Upload
  useLazyDownloadOfficesTemplateQuery,
  useUploadOfficesMutation,
  useGetFailedOfficesJobsQuery,
  useGetFailedOfficesRecordsQuery,
  // Custom employee fields
  useGetCustomEmployeeFieldsQuery,
  useGetActiveCustomEmployeeFieldsQuery,
  useSaveCustomEmployeeFieldMutation,
  useDeleteCustomEmployeeFieldMutation,
} = organizationApi;

// Export department and designation hooks directly from emp-dashboard
export {
  useCreateDepartmentMutation,
  useCreateDesignationMutation,
  useDeleteDepartmentMutation,
  useDeleteDesignationMutation,
  useGetAllDepartmentsQuery,
  useGetAllDesignationsQuery,
  useGetDepartmentDetailsQuery,
  useGetDesignationDetailsQuery,
  useUpdateDepartmentMutation,
  useUpdateDesignationMutation,
} from '../../employee/emp-dashboard/emp-dashboard.api';
